import { Callout, InputGroup } from "@blueprintjs/core";

import { Box } from "@/components/layout/flexbox";
import { isValidHttpOrHttpsUrl } from "@/components/lib/url";
import { ComponentMetadata } from "@/components/pages_logged_in/program/components/componentMetadata";
import { UrlLink } from "@/components/pages_logged_in/program/components/program-component/UrlLink";
import { StyledFormGroup } from "@/components/pages_logged_in/program/components/set-components/StyledFormGroup";
import {
  LinkPreview,
  LinkPreviewMetadata,
} from "@/components/pieces/link/LinkPreview";
import { VideoEmbed } from "@/components/pieces/videoEmbed";

/**
 * Props for the VideoAndArticleFormInput component
 * Generated by Copilot
 */
interface VideoAndArticleFormInputProps {
  /** Metadata for the component */
  componentMetadata: ComponentMetadata;
  /** The URL link for the video or article */
  link?: string | null;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Metadata for the link preview */
  linkPreviewMetadata?: LinkPreviewMetadata;
  /** Handler for when the link or prompt changes */
  onChange: (value: { link: string; prompt: string }) => void;
  /** Handler for when the link preview metadata is saved */
  onLinkSave: (value: LinkPreviewMetadata | undefined) => void;
}

/**
 * Component for inputting and displaying video or article URLs
 * with appropriate previews based on the content type.
 *
 * @param props - The component props
 * @returns A form input for video or article links with preview
 * Generated by Copilot
 */
export function VideoAndArticleFormInput({
  componentMetadata,
  link = "",
  disabled = false,
  linkPreviewMetadata,
  onChange,
  onLinkSave,
}: VideoAndArticleFormInputProps) {
  const _isVideo = componentMetadata?.isVideo;
  const _label = _isVideo ? "Link to Video" : "Link to Article";
  const _prompt = _isVideo ? "Video Component" : "Article Component";
  const _isValidUrl = link && isValidHttpOrHttpsUrl(link);

  /**
   * Handles changes to the link input
   * Generated by Copilot
   */
  const handleLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      link: e.target.value,
      prompt: _prompt,
      ...(componentMetadata.isVideo ? { metadata: undefined } : {}),
    });
  };

  return (
    <StyledFormGroup label={_label} subLabel={componentMetadata.linkDesc}>
      <InputGroup
        value={link ?? undefined}
        onChange={handleLinkChange}
        large={true}
        fill={true}
        placeholder={"https://"}
        disabled={disabled}
      />

      {/* link preview for article */}
      {componentMetadata.isArticle && _isValidUrl && (
        <LinkPreview
          url={link}
          isEditable={true}
          metadata={linkPreviewMetadata}
          onSave={onLinkSave}
        />
      )}

      {/* video embed */}
      {componentMetadata.isVideo && _isValidUrl && (
        <VideoEmbed
          url={link}
          altComponent={
            <Box>
              <Callout intent="warning">
                Video preview is not supported for this URL, but users can click
                the link below to watch the video directly.
              </Callout>
              <Box mt={2}>
                <UrlLink url={link} />
              </Box>
            </Box>
          }
        />
      )}
    </StyledFormGroup>
  );
}
